import { useMemo, useState } from "react";
import { fmtCFA } from "@/lib/format";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, Apple, CalendarClock } from "lucide-react";
import Thumb from "@/components/Thumb";

export const ABONNEMENTS = [
  { nom: "Abonnement Mensuel", prix: 3000, code: "mensuel" },
  { nom: "Abonnement Trimestriel", prix: 8000, code: "trimestriel" },
  { nom: "Abonnement Annuel", prix: 25000, code: "annuel" },
  { nom: "Carte 10 séances", prix: 4500, code: null },
];

const slug = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-").toLowerCase();
const tileCls = "text-left bg-background border border-border rounded-xl p-4 hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

export default function ProductGrid({ produits, seances, caisseOuverte, onAdd, onPickAbonnement }) {
  const [tab, setTab] = useState("produits");
  const [search, setSearch] = useState("");
  const q = search.toLowerCase();
  const produitsFiltres = useMemo(() => produits.filter((p) => p.nom.toLowerCase().includes(q)), [produits, q]);
  const seancesFiltrees = useMemo(() => seances.filter((s) => s.nom.toLowerCase().includes(q)), [seances, q]);

  const clickAbonnement = (a) => {
    if (!caisseOuverte) return toast.error("Ouvrez la caisse avant de vendre");
    if (a.code) onPickAbonnement(a);
    else onAdd({ type: "abonnement", ref_id: null, nom: a.nom, prix: a.prix });
  };

  return (
    <Card className="bg-card border-border lg:col-span-8" data-testid="pos-catalog">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-background border border-border" data-testid="pos-tabs">
              <TabsTrigger value="produits" data-testid="tab-produits">Produits</TabsTrigger>
              <TabsTrigger value="seances" data-testid="tab-seances">Séances</TabsTrigger>
              <TabsTrigger value="abonnements" data-testid="tab-abonnements">Abonnements</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" data-testid="pos-search-input" className="pl-9 bg-background border-border text-foreground w-56" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={tab}>
          <TabsContent value="produits" className="mt-0">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {produitsFiltres.map((p) => (
                <button key={p.id} data-testid={`pos-produit-${p.id}`} disabled={p.stock === 0} className={tileCls}
                  onClick={() => onAdd({ type: "produit", ref_id: p.id, nom: p.nom, prix: p.prix, stockMax: p.stock })}>
                  <div className="flex items-center gap-3">
                    <Thumb path={p.image_path} alt={p.nom} icon={Apple} className="w-12 h-12" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{p.nom}</p>
                      <p className="text-xs text-muted-foreground">{p.categorie}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-emerald-600 dark:text-emerald-400 font-bold text-sm">{fmtCFA(p.prix)}</span>
                    <Badge variant="outline" className={p.stock <= p.seuil_alerte ? "border-red-500/40 text-red-600 dark:text-red-400" : "border-border text-muted-foreground"}>Stock {p.stock}</Badge>
                  </div>
                </button>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="seances" className="mt-0">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {seancesFiltrees.map((s) => (
                <button key={s.id} data-testid={`pos-seance-${s.id}`} className={tileCls} onClick={() => onAdd({ type: "seance", ref_id: s.id, nom: s.nom, prix: s.prix })}>
                  <div className="flex items-center gap-3">
                    <Thumb path={s.image_path} alt={s.nom} icon={CalendarClock} className="w-12 h-12" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{s.nom}</p>
                      <p className="text-xs text-muted-foreground">{s.type} — {s.horaire}</p>
                    </div>
                  </div>
                  <span className="block mt-3 text-emerald-600 dark:text-emerald-400 font-bold text-sm">{fmtCFA(s.prix)}</span>
                </button>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="abonnements" className="mt-0">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {ABONNEMENTS.map((a) => (
                <button key={a.nom} data-testid={`pos-abonnement-${slug(a.nom)}`} className={tileCls} onClick={() => clickAbonnement(a)}>
                  <p className="text-sm font-semibold text-foreground">{a.nom}</p>
                  <span className="block mt-3 text-emerald-600 dark:text-emerald-400 font-bold text-sm">{fmtCFA(a.prix)}</span>
                </button>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
